import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useSeasons } from "../lib/queries/memberships";
import { seasonOrdinal } from "../lib/seasonPhase";
import { SeasonContext, type SeasonContextValue } from "./seasonContext";
import type { ID } from "../lib/id";

const STORAGE_KEY = "cmfis.selected_season_id";

function readStored(): ID | null {
  try {
    // A value from before the switch to UUIDs matches no season, and the
    // provider falls back to the active one.
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

// SeasonProvider exposes the global "which season am I looking at" state.
// It defaults to the active season and persists an explicit choice to
// localStorage. Writes only ever target the active season (enforced by
// the backend); a non-active selection is a read-only historical view.
export function SeasonProvider({ children }: { children: ReactNode }) {
  const { data: seasons = [], isLoading } = useSeasons();
  const [selectedSeasonId, setSelectedIdState] = useState<ID | null>(() =>
    readStored(),
  );

  const setSelectedSeasonId = useCallback((id: ID | null) => {
    setSelectedIdState(id);
    try {
      if (id === null) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, String(id));
    } catch {
      // ignore storage failures (private mode, quota, etc.)
    }
  }, []);

  const activeSeason = useMemo(() => seasons.find((s) => s.active), [seasons]);

  const selectedSeason = useMemo(() => {
    if (selectedSeasonId !== null) {
      const found = seasons.find((s) => s.id === selectedSeasonId);
      if (found) return found;
    }
    return activeSeason;
  }, [seasons, selectedSeasonId, activeSeason]);

  // Drop a stale stored id once seasons have loaded and it no longer exists.
  useEffect(() => {
    if (
      selectedSeasonId !== null &&
      seasons.length > 0 &&
      !seasons.some((s) => s.id === selectedSeasonId)
    ) {
      setSelectedSeasonId(null);
    }
  }, [seasons, selectedSeasonId, setSelectedSeasonId]);

  const isActiveSelected =
    !selectedSeason || !activeSeason || selectedSeason.id === activeSeason.id;

  // Ordered by the year in the code, the same rule the server applies —
  // Season carries no start date, only a free-text code.
  //
  // A code with no year in it answers null, and then the question is not
  // asked: "not placeable" is not evidence of "past", and the server
  // allows in exactly that case too (season.Ordinal's second value).
  const selectedOrdinal = selectedSeason ? seasonOrdinal(selectedSeason.code) : null;
  const activeOrdinal = activeSeason ? seasonOrdinal(activeSeason.code) : null;
  const isSelectedPast =
    !isActiveSelected &&
    selectedOrdinal !== null &&
    activeOrdinal !== null &&
    selectedOrdinal < activeOrdinal;

  const value = useMemo<SeasonContextValue>(
    () => ({
      seasons,
      activeSeason,
      selectedSeason,
      isActiveSelected,
      isSelectedPast,
      isLoading,
      setSelectedSeasonId,
    }),
    [
      seasons,
      activeSeason,
      selectedSeason,
      isActiveSelected,
      isSelectedPast,
      isLoading,
      setSelectedSeasonId,
    ],
  );

  return (
    <SeasonContext.Provider value={value}>{children}</SeasonContext.Provider>
  );
}
